import { type GenerateContentResponseUsageMetadata } from "@google/genai";
import { type UsageStats } from "@mastra/core/observability";
import { z, ZodTypeAny } from "zod";

/**
 * Single entry point for turning a Zod schema into the JSON Schema we hand to an LLM.
 *
 * Lives here because this module is already the shared JSON-Schema utility for LLM-facing
 * schemas; `openai-schema.ts` and the model files reuse the exact same base conversion so the
 * options below only have to be reasoned about once.
 *
 * `target: "draft-7"` — matches the dialect zod-to-json-schema emitted before the zod v4
 * migration and, more importantly, makes zod emit sub-schemas under `definitions`. The v4
 * default (`draft-2020-12`) uses `$defs`, which the Gemini cleanup below does not strip.
 *
 * `io: "input"` — these schemas describe the JSON an LLM must emit, which we then feed back
 * through `schema.parse(...)`, so the *input* side is the correct projection. It is also
 * mandatory rather than merely preferable: several shared field schemas (`simpleDateSchema`,
 * `phoneNumberSchema`, `npiNumberSchema`, `ssnSchema`) are `.transform()` pipes, and zod v4
 * throws "Transforms cannot be represented in JSON Schema" under the default `io: "output"`.
 * It also keeps `.default()`-bearing properties out of `required`, which is what the previous
 * zod-to-json-schema output did.
 */
export function toLlmJsonSchema(schema: ZodTypeAny): Record<string, unknown> {
  return z.toJSONSchema(schema, { target: "draft-7", io: "input" }) as Record<string, unknown>;
}

/**
 * Marks a property so OpenAI strict mode emits it as required-and-**non**-nullable rather than the
 * required-and-nullable form an `.optional()` property normally gets (see `applyOpenAiStrictRules`).
 *
 * Exists to preserve one pre-v4 emission quirk. `zod-to-json-schema`'s `openAi` target read
 * optionality off the outermost wrapper, so a `z.preprocess(fn, inner.optional())` — a `ZodEffects`
 * in v3 — read as non-optional and emitted with no null branch, while a bare `inner.optional()`
 * correctly got one. zod v4 sees through the pipe and reports both as optional, so both now get a
 * null branch and the model gains permission to answer `null` where it previously had to commit.
 *
 * Only meaningful on a property of an object schema, and only for the OpenAI path: the key is
 * `$`-prefixed so `sanitizeSchema` drops it from the Gemini schema, and `applyOpenAiStrictRules`
 * consumes and strips it, so it never reaches a provider.
 */
export const EMIT_NON_NULLABLE_KEYWORD = "$vhEmitNonNullable";

/** See {@link EMIT_NON_NULLABLE_KEYWORD}. */
export function emitNonNullable<T extends ZodTypeAny>(schema: T): T {
  return schema.meta({ [EMIT_NON_NULLABLE_KEYWORD]: true }) as unknown as T;
}

import { summarizeZodIssues } from "@/shared/utils/zod-issue-summary";
import { logAndThrow } from "@/src/lib/logging/logger";

// Utility to recursively replace { const: value } with { enum: [value] }
function fixConstToEnum(schema: unknown): unknown {
  if (Array.isArray(schema)) {
    return schema.map(fixConstToEnum);
  }

  if (schema && typeof schema === "object") {
    const obj = schema as Record<string, unknown>;
    const newObj: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      if (key === "const") {
        newObj.enum = [value];
      } else {
        newObj[key] = fixConstToEnum(value);
      }
    }

    return newObj;
  }

  return schema;
}

function convertTypesToNullable(schema: any): any {
  if (Array.isArray(schema)) {
    return schema.map(convertTypesToNullable);
  }

  if (schema && typeof schema === "object") {
    if (schema.additionalProperties === false) {
      delete schema.additionalProperties;
    }

    // Zod v4 emits `oneOf` for discriminated unions where v3 emitted `anyOf`. Both collapse to the
    // first non-null variant here (see ADR 18); without this, `oneOf` would fall through to the
    // UNSUPPORTED_GEMINI_KEYWORDS strip below and leave an empty, entirely unconstrained schema.
    if (Array.isArray(schema.anyOf) || Array.isArray(schema.oneOf)) {
      const list = schema.anyOf ?? schema.oneOf;

      delete schema.anyOf;
      delete schema.oneOf;

      const nonNull = list.find((s: { type?: string }) => s?.type !== "null");
      const hadNull = list.some((s: { type?: string }) => s?.type === "null");

      if (nonNull) {
        const mergedSchema = {
          ...convertTypesToNullable(nonNull),
          ...(hadNull ? { nullable: true } : {}),
          ...(schema.description ? { description: schema.description } : {}),
        };

        for (const key in schema) {
          if (mergedSchema[key] === undefined) {
            mergedSchema[key] = convertTypesToNullable(schema[key]);
          }
        }

        return mergedSchema;
      }

      if (hadNull) {
        return {
          type: "string",
          nullable: true,
          description: schema.description,
        };
      }
    }

    if (Array.isArray(schema.type)) {
      const hadNull = schema.type.includes("null");
      const nonNullTypes = schema.type.filter((t: string) => t !== "null");

      if (nonNullTypes.length > 0) {
        schema.type = nonNullTypes[0];
      }

      if (hadNull) {
        schema.nullable = true;
      }
    }

    for (const key in schema) {
      schema[key] = convertTypesToNullable(schema[key]);
    }
  }

  return schema;
}

export const GEMINI_MAX_ENUM_LENGTH = 50;

const UNSUPPORTED_GEMINI_KEYWORDS = new Set([
  "exclusiveMinimum",
  "exclusiveMaximum",
  "minimum",
  "maximum",
  "minLength",
  "maxLength",
  "minItems",
  "maxItems",
  "pattern",
  "propertyNames",
  "anyOf",
  "allOf",
  "oneOf",
]);

export function appendAllowedValuesToDescription(existingDescription: unknown, enumValues: unknown[]): string {
  const allowedValuesBlock = [
    "Allowed values (must match one of these exactly):",
    ...enumValues.map((value) => `- ${typeof value === "string" ? JSON.stringify(value) : String(value)}`),
  ].join("\n");

  if (typeof existingDescription === "string" && existingDescription.trim()) {
    return `${existingDescription.trimEnd()}\n\n${allowedValuesBlock}`;
  }

  return allowedValuesBlock;
}

// Strip properties that start with $ and empty enums, and keywords unsupported by Gemini
function sanitizeSchema(schema: unknown): unknown {
  if (Array.isArray(schema)) {
    return schema.map(sanitizeSchema);
  }

  if (!schema || typeof schema !== "object") {
    return schema;
  }

  const sanitized = Object.fromEntries(
    Object.entries(schema as Record<string, unknown>)
      .filter(([key]) => !key.startsWith("$") && !UNSUPPORTED_GEMINI_KEYWORDS.has(key))
      .map(([key, value]) => [key, sanitizeSchema(value)])
  ) as Record<string, unknown>;

  if (Array.isArray(sanitized.enum)) {
    const filteredEnum = sanitized.enum.filter((value) => !(typeof value === "string" && value.length === 0));

    if (filteredEnum.length > GEMINI_MAX_ENUM_LENGTH) {
      delete sanitized.enum;
      sanitized.description = appendAllowedValuesToDescription(sanitized.description, filteredEnum);
    } else if (filteredEnum.length > 0) {
      sanitized.enum = filteredEnum;
    } else {
      delete sanitized.enum;
    }
  }

  return sanitized;
}

/** Rewrites an already-emitted JSON Schema into the subset Google GenAI accepts. Mutates `raw`. */
function geminiSchemaFromJson(raw: Record<string, unknown>): Record<string, unknown> {
  const converted = convertTypesToNullable(raw);

  // Google GenAI rejects a `definitions` block. zod only emits one for a self-referential
  // schema, but strip it defensively — `sanitizeSchema` below only drops `$`-prefixed keys.
  if (converted.definitions) {
    delete converted.definitions;
  }

  return sanitizeSchema(fixConstToEnum(converted)) as Record<string, unknown>;
}

export function getGeminiCompatibleSchema(schema: ZodTypeAny): Record<string, unknown> {
  return geminiSchemaFromJson(toLlmJsonSchema(schema));
}

// Extraction schemas describe every possible field, but absent document values do not need
// placeholders. Remove object-level required lists while preserving the field definitions so
// structured-output models can omit properties they did not find.
export function makeJsonSchemaPropertiesOptional(schema: unknown): Record<string, unknown> {
  const makeOptional = (node: unknown): unknown => {
    if (Array.isArray(node)) {
      return node.map(makeOptional);
    }

    if (!node || typeof node !== "object") {
      return node;
    }

    return Object.fromEntries(
      Object.entries(node as Record<string, unknown>)
        .filter(([key]) => key !== "required")
        .map(([key, value]) => [key, makeOptional(value)])
    );
  };

  return makeOptional(schema) as Record<string, unknown>;
}

export function toProcessedUsageMetadata(rawUsageMetadata: GenerateContentResponseUsageMetadata | undefined) {
  return {
    cachedInputTokens: rawUsageMetadata?.cachedContentTokenCount ?? 0,
    uncachedInputTokens: (rawUsageMetadata?.promptTokenCount ?? 0) - (rawUsageMetadata?.cachedContentTokenCount ?? 0),
    thinkingOutputTokens: rawUsageMetadata?.thoughtsTokenCount ?? 0,
    visibleOutputTokens: rawUsageMetadata?.candidatesTokenCount ?? 0,
  };
}

export type ProcessedUsageMetadata = ReturnType<typeof toProcessedUsageMetadata>;

type GeminiTokenCount = "promptTokenCount" | "cachedContentTokenCount" | "candidatesTokenCount" | "thoughtsTokenCount";

export function toGeminiUsageStats(usage: Partial<Record<GeminiTokenCount, number | null>> | undefined): UsageStats {
  const reasoning = usage?.thoughtsTokenCount ?? undefined;
  const text = usage?.candidatesTokenCount ?? undefined;

  return {
    inputTokens: usage?.promptTokenCount ?? undefined,
    outputTokens: text === undefined && reasoning === undefined ? undefined : (text ?? 0) + (reasoning ?? 0),
    inputDetails: { cacheRead: usage?.cachedContentTokenCount ?? undefined },
    outputDetails: { text, reasoning },
  };
}

export function parseGeminiJsonResponse<S extends z.ZodTypeAny>(
  responseText: string | undefined | null,
  schema: S,
  // Callers must provide only non-sensitive diagnostics, never generated content.
  meta: Record<string, unknown> = {}
): z.infer<S> {
  const diagnostics = { ...meta, responseLength: responseText?.length ?? 0 };

  if (!responseText) {
    logAndThrow("Gemini returned no response text", diagnostics);
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(responseText);
  } catch {
    // JSON parse errors can include excerpts of the response.
    logAndThrow("Invalid JSON from Gemini API", diagnostics);
  }

  const result = schema.safeParse(parsed);

  if (!result.success) {
    // Validation messages and input values may contain patient information; the summaries keep only
    // schema paths (non-identifier keys masked) and codes.
    logAndThrow("Unable to parse gemini response", {
      ...diagnostics,
      issueCodes: result.error.issues.map((issue) => issue.code),
      issues: summarizeZodIssues(result.error),
    });
  }

  return result.data;
}
